import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Grid, Card, Box } from "@material-ui/core";
import MapGL, { Marker } from "react-map-gl";

import { DataGridCystellarTable } from "../util/DataGridCystellarTable";
import httpClient from "../../lib/httpClient";
import { apiError } from "../../lib/apiError";
import { stringFilterOperators, tableCellRender, formatDateValue } from "../util/tableUtils";
import { defaultRiskAssessmentMapStyle, defaultRomeLatitude, defaultRomeLongitude } from "../map/Constants";
import { getImageForHazard, goToEventPage } from "./emergencyUtils";
import { EmergencyPopupDetails } from "./EmergencyPopupDetails";
import { ScaleBar } from "../map/ScaleBar";
import { MapStylesMenu } from "../map/MapStylesMenu";

const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
if (!TOKEN) {
  console.error("REACT_APP_MAPBOX_TOKEN was not defined");
}

export const EmergencyMonitoring = () => {
  const { t } = useTranslation();
  const mapRef = useRef(null);

  const [viewState, setViewState] = useState<PartialViewState>({
    latitude: defaultRomeLatitude,
    longitude: defaultRomeLongitude,
    zoom: 6,
  });

  const [emergencyData, setEmergencyData] = useState<TerraShieldOverview[]>([]);
  const [selectedOverview, setSelectedOverview] = useState<TerraShieldOverview | null>();
  const [isLoading, setIsLoading] = useState<Boolean>(false);
  const [mapStyle, setMapStyle] = useState<string>(defaultRiskAssessmentMapStyle);

  const getEmergencyData = async () => {
    setIsLoading(true);

    try {
      const { data } = await httpClient().get(`terrashield/overview`);

      setEmergencyData(data.data);
      setIsLoading(false);
    } catch (error) {
      apiError(error);

      setEmergencyData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getEmergencyData();
  }, []);

  const onMove = (viewState: PartialViewState) => {
    setViewState(viewState);
  };

  const emergencyColumns = [
    {
      field: "code",
      headerName: t("emergencyMonitoring.code"),
      sortable: false,
    },
    {
      field: "name",
      headerName: t("emergencyMonitoring.name"),
      sortable: false,
    },
    {
      field: "category",
      headerName: t("emergencyMonitoring.category"),
      sortable: false,
    },
    {
      field: "eventTime",
      headerName: t("emergencyMonitoring.eventTime"),
      sortable: false,
      valueGetter: (params: { row: TerraShieldOverview }) => formatDateValue(params.row.eventTime),
    },
    {
      field: "n_aois",
      headerName: t("emergencyMonitoring.numberOfAois"),
      sortable: false,
    },
  ];

  const columns = emergencyColumns.map((tableColumn) => ({
    valueFormatter: (params: any) => tableCellRender(params.value?.toString()),
    filterOperators: stringFilterOperators,
    flex: 1,
    ...tableColumn,
  }));

  const onSelectMarker = (overview: TerraShieldOverview) => {
    if (overview.code === selectedOverview?.code) {
      setSelectedOverview(null);
    } else {
      setSelectedOverview(overview);
    }
  };

  return (
    <Grid style={{ marginTop: 25 }}>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={12}>
          <Box>
            <Card>
              <MapGL
                {...viewState}
                ref={mapRef}
                mapboxAccessToken={TOKEN}
                style={{ width: "100%", height: "620px" }}
                mapStyle={mapStyle}
                onMove={(evt) => onMove(evt.viewState)}
              >
                {emergencyData.map((overview, index) => (
                  <Marker latitude={overview.lat} longitude={overview.lng} key={index}>
                    <img
                      src={getImageForHazard(overview.category as EmergencyHazardType)}
                      alt="Example"
                      style={{ width: 35, height: 35, display: "block" }}
                      onClick={() => onSelectMarker(overview)}
                    />
                  </Marker>
                ))}

                {selectedOverview && <EmergencyPopupDetails overview={selectedOverview} />}
                <MapStylesMenu setMapStyle={setMapStyle}></MapStylesMenu>
                <ScaleBar zoom={viewState.zoom} />
              </MapGL>
            </Card>
          </Box>
        </Grid>
        <Grid item xs={12} sm={12}>
          <div style={{ height: 700 }}>
            <DataGridCystellarTable
              columns={columns}
              rows={emergencyData}
              rowCount={emergencyData.length}
              getRowId={(rowData: TerraShieldOverview) => rowData._id}
              loading={isLoading}
              onRowClick={(rowData: { row: TerraShieldOverview }) => goToEventPage(rowData.row.code)}
            />
          </div>
        </Grid>
      </Grid>
    </Grid>
  );
};
